/**
 * Phase L (Kapitel 7.1, Gruppe D #1 — Entscheidung Shadow: Wiederherstellungs-
 * Code für den Chef) — alles rund um den persönlichen Lohn-Schlüssel:
 *
 *  - RecoveryCodeModal: zeigt einen gerade erzeugten Wiederherstellungs-Code
 *    GENAU EINMAL an (der Server kann ihn danach selbst nicht mehr lesen).
 *    Schließen erst nach Bestätigung „sicher notiert“.
 *  - WageKeyBanner: Hinweis, wenn der Schlüssel gesperrt ist (nach einem
 *    Passwort-Reset → mit Code entsperren oder neu beginnen) bzw. wenn diese
 *    Sitzung keinen Schlüssel hat (einmal neu anmelden).
 *  - RecoveryCodeCard: Profil-Karte für Chefs — neuen Code erzeugen.
 */

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth, type AuthResponseWithRecovery } from '../store/auth';
import { apiFetch, ApiError } from '../lib/api';
import { hapticError, hapticSuccess } from '../lib/haptics';

function Modal({ children, onBackdrop }: { children: ReactNode; onBackdrop?: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onBackdrop?.();
      }}
    >
      <div className="bg-slate-800 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-md max-h-[90vh] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

/** Einmal-Anzeige des Wiederherstellungs-Codes (global in App.tsx eingebunden). */
export function RecoveryCodeModal() {
  const code = useAuth((s) => s.pendingRecoveryCode);
  const setCode = useAuth((s) => s.setPendingRecoveryCode);
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!code) return null;

  async function copy() {
    try {
      await navigator.clipboard.writeText(code!);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Modal>
      <h3 className="text-lg font-semibold mb-2">Dein Wiederherstellungs-Code</h3>
      <p className="text-sm text-slate-300 mb-3">
        Die Löhne deiner Firma sind verschlüsselt — lesbar nur mit deinem Passwort. Vergisst du dein Passwort und setzt
        es zurück, brauchst du diesen Code, um wieder an die Lohndaten zu kommen.{' '}
        <strong>Er wird nur jetzt angezeigt</strong> — auch der Betreiber kann ihn nicht wiederherstellen. (Nur im
        äußersten Notfall kann der Betreiber mit seinem offline verwahrten Notfall-Schlüssel die Lohndaten neu
        freischalten.)
      </p>
      <div className="rounded bg-slate-900 border border-amber-500/40 p-3 text-center font-mono text-base sm:text-lg tracking-wider select-all break-all">
        {code}
      </div>
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={copy} className="text-xs bg-surface-3 hover:bg-slate-600 rounded px-3 py-1.5">
          {copied ? 'Kopiert ✓' : 'Kopieren'}
        </button>
      </div>
      <p className="text-xs text-slate-400 mt-3">
        Tipp: ausdrucken oder im Passwort-Manager speichern — nicht in dieser App und nicht per Mail an dich selbst.
      </p>
      <label className="flex items-start gap-2 mt-4 text-sm">
        <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-1" />
        <span>Ich habe den Code sicher notiert.</span>
      </label>
      <button
        type="button"
        disabled={!confirmed}
        onClick={() => {
          setCode(null);
          setConfirmed(false);
          setCopied(false);
        }}
        className="btn w-full mt-4 disabled:opacity-50"
      >
        Fertig
      </button>
    </Modal>
  );
}

/** Hinweis-Leiste unter dem Header, wenn die Löhne gerade nicht entschlüsselt werden können. */
export function WageKeyBanner() {
  const user = useAuth((s) => s.user);
  const refreshMe = useAuth((s) => s.refreshMe);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const status = user?.wageKeyStatus;

  // 'pending': der Server versiegelt den eigenen Lohn im Hintergrund nach —
  // jede Minute nachsehen und danach alle Beträge neu laden.
  const wasPending = useRef(false);
  useEffect(() => {
    if (status === 'pending') {
      wasPending.current = true;
      const t = window.setInterval(() => void refreshMe().catch(() => undefined), 60_000);
      return () => window.clearInterval(t);
    }
    if (status === 'ok' && wasPending.current) {
      wasPending.current = false;
      void qc.invalidateQueries();
    }
    return undefined;
  }, [status, refreshMe, qc]);

  if (!user || user.passwordIsInitial || !status || status === 'ok' || status === 'none') return null;

  if (status === 'pending') {
    return (
      <div className="bg-sky-500/10 border-b border-sky-500/30 text-sky-200 text-xs sm:text-sm">
        <div className="mx-auto w-full max-w-7xl px-3 sm:px-6 py-2">
          Dein Lohn wird gerade für dich freigeschaltet (Lohn-Verschlüsselung). Das passiert automatisch, sobald dein
          Chef die App nutzt — bis dahin werden deine Beträge nicht angezeigt.
        </div>
      </div>
    );
  }

  if (status === 'relogin') {
    return (
      <div className="bg-amber-500/10 border-b border-amber-500/30 text-amber-200 text-xs sm:text-sm">
        <div className="mx-auto w-full max-w-7xl px-3 sm:px-6 py-2">
          Löhne sind jetzt verschlüsselt. Bitte melde dich <strong>einmal ab und wieder an</strong>, damit Beträge
          wieder angezeigt werden.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-red-500/10 border-b border-red-500/30 text-red-200 text-xs sm:text-sm">
      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6 py-2 flex flex-wrap items-center justify-between gap-2">
        <span>
          Dein Lohn-Schlüssel ist nach dem Zurücksetzen des Passworts gesperrt — Beträge werden erst nach dem
          Entsperren mit deinem Wiederherstellungs-Code wieder angezeigt.
        </span>
        <button onClick={() => setOpen(true)} className="text-xs bg-red-600 hover:bg-red-500 text-white rounded px-3 py-1.5">
          Entsperren
        </button>
      </div>
      {open && <UnlockModal onClose={() => setOpen(false)} />}
    </div>
  );
}

function UnlockModal({ onClose }: { onClose: () => void }) {
  const token = useAuth((s) => s.token);
  const setUser = useAuth((s) => s.setUser);
  const setPendingRecoveryCode = useAuth((s) => s.setPendingRecoveryCode);
  const qc = useQueryClient();
  const [mode, setMode] = useState<'recover' | 'reset'>('recover');
  const [password, setPassword] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [resetConfirmed, setResetConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setError(null);
    setBusy(true);
    try {
      const data = await apiFetch<AuthResponseWithRecovery>(
        mode === 'recover' ? '/api/auth/recover-keys' : '/api/auth/reset-keys',
        { method: 'POST', token, body: mode === 'recover' ? { password, recoveryCode } : { password } },
      );
      hapticSuccess();
      setUser(data.user);
      if (data.recoveryCode) setPendingRecoveryCode(data.recoveryCode);
      // Alle Daten mit Beträgen neu laden (jetzt entschlüsselbar).
      void qc.invalidateQueries();
      onClose();
    } catch (err) {
      hapticError();
      setError(err instanceof ApiError ? err.message : 'Das hat nicht geklappt.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onBackdrop={onClose}>
      <h3 className="text-lg font-semibold mb-3">Lohn-Schlüssel entsperren</h3>
      <div className="flex gap-2 mb-4 text-xs">
        <button
          type="button"
          onClick={() => setMode('recover')}
          className={`rounded px-3 py-1.5 ${mode === 'recover' ? 'bg-emerald-600 text-white' : 'bg-surface-3'}`}
        >
          Mit Code entsperren
        </button>
        <button
          type="button"
          onClick={() => setMode('reset')}
          className={`rounded px-3 py-1.5 ${mode === 'reset' ? 'bg-emerald-600 text-white' : 'bg-surface-3'}`}
        >
          Code verloren
        </button>
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block">
          <span className="block text-sm text-slate-300 mb-1">Dein (neues) Passwort</span>
          <input
            required
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
            autoComplete="current-password"
          />
        </label>
        {mode === 'recover' ? (
          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">Wiederherstellungs-Code</span>
            <input
              required
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              className="input font-mono tracking-wider uppercase"
              placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
        ) : (
          <>
            <p className="text-sm text-amber-200">
              Ohne Code bekommst du einen <strong>neuen</strong> Schlüssel. Beträge, die bisher nur für deinen alten
              Schlüssel lesbar waren, siehst du wieder, sobald jemand anderes mit Zugriff darauf (z. B. ein weiterer
              Chef oder die jeweilige Person selbst) angemeldet ist. Hast du als einziger Chef den Code verloren und
              meldet sich niemand sonst an, kann der Betreiber (Admin) sie mit seinem Notfall-Schlüssel wieder
              freischalten.
            </p>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={resetConfirmed}
                onChange={(e) => setResetConfirmed(e.target.checked)}
                className="mt-1"
              />
              <span>Verstanden — neu beginnen.</span>
            </label>
          </>
        )}
        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={busy || (mode === 'reset' && !resetConfirmed)}
            className="btn flex-1 disabled:opacity-50"
          >
            {busy ? '…' : mode === 'recover' ? 'Entsperren' : 'Neu beginnen'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Profil-Karte (nur Chefs): Wiederherstellungs-Code neu erzeugen. */
export function RecoveryCodeCard({ isChef }: { isChef: boolean }) {
  const user = useAuth((s) => s.user);
  const [open, setOpen] = useState(false);
  if (!user || !isChef || user.wageKeyStatus === 'none') return null;
  const canCreate = user.wageKeyStatus === 'ok' || user.wageKeyStatus === 'pending';

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">Wiederherstellungs-Code</p>
          <p className="text-xs text-slate-400">
            {user.hasRecoveryCode ? 'Hinterlegt' : 'Noch keiner hinterlegt'} — entsperrt die Lohndaten nach einem
            Passwort-Reset.
          </p>
        </div>
        {canCreate ? (
          <button onClick={() => setOpen(true)} className="text-xs bg-surface-3 hover:bg-slate-600 rounded px-3 py-1.5 shrink-0">
            Neu erzeugen
          </button>
        ) : (
          <span className="text-xs text-slate-400 shrink-0">
            {user.wageKeyStatus === 'locked' ? 'Erst entsperren' : 'Nach Neu-Anmeldung'}
          </span>
        )}
      </div>
      {open && <NewRecoveryCodeModal onClose={() => setOpen(false)} />}
    </div>
  );
}

function NewRecoveryCodeModal({ onClose }: { onClose: () => void }) {
  const token = useAuth((s) => s.token);
  const refreshMe = useAuth((s) => s.refreshMe);
  const setPendingRecoveryCode = useAuth((s) => s.setPendingRecoveryCode);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setError(null);
    setBusy(true);
    try {
      const data = await apiFetch<{ recoveryCode: string }>('/api/auth/recovery-code', {
        method: 'POST',
        token,
        body: { password },
      });
      hapticSuccess();
      setPendingRecoveryCode(data.recoveryCode);
      void refreshMe().catch(() => undefined);
      onClose();
    } catch (err) {
      hapticError();
      setError(err instanceof ApiError ? err.message : 'Code konnte nicht erzeugt werden.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onBackdrop={onClose}>
      <h3 className="text-lg font-semibold mb-2">Neuen Wiederherstellungs-Code erzeugen</h3>
      <p className="text-sm text-slate-300 mb-3">Der bisherige Code wird dabei ungültig.</p>
      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block">
          <span className="block text-sm text-slate-300 mb-1">Dein Passwort</span>
          <input
            required
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
            autoComplete="current-password"
          />
        </label>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <div className="flex gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-ghost flex-1">
            Abbrechen
          </button>
          <button type="submit" disabled={busy} className="btn flex-1 disabled:opacity-50">
            {busy ? '…' : 'Erzeugen'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
